import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { apiCall } from '../utils/api';

function Resolution() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Get data passed from the IncidentView page via the URL
  const runbookId = searchParams.get('runbook_id');
  // const moduleIndex = parseInt(searchParams.get('module_index') || '0'); // OLD: Module-based navigation
  const incidentNumber = searchParams.get('incident_number');

  // Component State - NEW: Using Steps directly from runbook
  const [runbookData, setRunbookData] = useState(null);
  const [steps, setSteps] = useState([]);
  // const [moduleData, setModuleData] = useState(null); // OLD: Module-based
  // const [totalModules, setTotalModules] = useState(0); // OLD: Module-based
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Feedback Modal State
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [runbookFeedback, setRunbookFeedback] = useState(null);
  const [runbookFeedbackReason, setRunbookFeedbackReason] = useState('');
  const [feedbackError, setFeedbackError] = useState(false);

  useEffect(() => {
    const fetchResolutionData = async () => {
      if (!runbookId) {
        setError("No Runbook ID provided.");
        setLoading(false);
        return;
      }
      try {
        // NEW: Fetch the entire runbook with Steps directly
        // Using /resolution/runbook which fetches from runbookTable
        const data = await apiCall(`/resolution/runbook/${runbookId}`);
        setRunbookData(data);
        
        // Get Steps directly from runbook (NEW structure)
        const runbookSteps = data.Steps || [];
        setSteps(runbookSteps);
        
        console.log(`[Resolution] Fetched runbook with ${runbookSteps.length} steps`);
        
        /* OLD: Module-based approach - COMMENTED OUT
        const modules = runbookData.Modules || [];
        setTotalModules(modules.length);

        // Check if there are more modules to display
        if (moduleIndex < modules.length) {
            // Get the current module's ID
            const currentModuleId = modules[moduleIndex]['Module ID'];
            
            // Fetch the specific module to get its detailed steps
            const detailedModuleData = await apiCall(`/resolution/module/${currentModuleId}`);
            setModuleData(detailedModuleData);
        } else {
            // User has finished all modules for this runbook
            setModuleData(null); 
            setShowFeedbackModal(true); // Automatically open feedback modal
        }
        */
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchResolutionData();
  }, [runbookId]);

  const handleSubmitRunbookFeedback = async () => {
    if (runbookFeedback === null) {
      setFeedbackError(true);
      return;
    }
    setFeedbackError(false);

    // Convert boolean to rating: true (helpful) = 5, false (not helpful) = 1
    const feedbackRating = runbookFeedback === true ? 5 : 1;

    const payload = {
      incident_number: incidentNumber,
      feedback: feedbackRating,
      feedback_reason: runbookFeedbackReason,
    };

    console.log('Submitting overall feedback:', payload);

    try {
      const response = await apiCall('/submit_feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      
      console.log('Overall feedback response:', response);
      alert("Thank you for your feedback!");
      navigate("/incident_view");
    } catch (error) {
      console.error('Overall feedback error:', error);
      alert(`Failed to submit feedback: ${error.message}`);
    }
  };

  const handleStepFeedback = async (rowIndex, feedbackValue) => {
    if (!steps || feedbackValue === "") return; // NEW: Check steps instead of moduleData

    const step = steps[rowIndex]; // NEW: Get step directly from steps array
    const reasonInput = document.getElementById(`reason-${rowIndex}`);
    const reason = reasonInput ? reasonInput.value : "";

    // NEW: Step feedback for runbook with Steps (no modules)
    const payload = {
      incident_number: incidentNumber,
      runbook_number: runbookId,
      // module_index: moduleIndex, // OLD: Not needed in new structure
      module_index: 0, // Keep for backward compatibility with backend
      step_number: rowIndex, // Use index (0-based)
      step_label: step['Step Number'] || step['step_number'] || `Step ${rowIndex + 1}`, // Handle different formats
      feedback: feedbackValue === 'useful' ? 1 : 0,
      feedback_reason: reason,
    };

    console.log('Submitting step feedback:', payload);

    try {
      const response = await apiCall('/submit_step_feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      
      console.log('Step feedback response:', response);
      // Feedback submitted silently - check console for confirmation
    } catch (error) {
      console.error('Step feedback error:', error);
      // Only show alert on error
      alert(`Failed to submit feedback: ${error.message}`);
    }
  };

  if (loading) return <div className="container-fluid mt-4">Loading resolution steps...</div>;
  if (error) return <div className="container-fluid mt-4 alert alert-danger">{error}</div>;

  // NEW: Render all steps from runbook directly (no module navigation)
  return (
    <div className="container-fluid mt-4">
      {runbookData && steps.length > 0 && (
        <div>
          <h1>Resolution Steps for Incident {incidentNumber}</h1>
          <div className="card p-3">
            <h2>{runbookData['Runbook Title'] || 'Runbook Steps'}</h2>
            <p><strong>Tribe:</strong> {runbookData.Tribe || 'N/A'}</p>
            <p><strong>Solution:</strong> {runbookData.Solution || 'N/A'}</p>
            <p><strong>Total Steps:</strong> {steps.length}</p>
            
            <div className="table-responsive">
              <table className="table table-bordered mt-3" style={{ tableLayout: 'fixed', width: '100%' }}>
              <thead className="table-dark">
                <tr>
                  <th style={{ width: '5%', textAlign: 'center' }}>Step</th>
                  <th style={{ width: '45%', textAlign: 'left' }}>Step Details</th>
                  <th style={{ width: '8%', textAlign: 'center' }}>Image</th>
                  <th style={{ width: '17%', textAlign: 'center' }}>Feedback</th>
                  <th style={{ width: '25%', textAlign: 'left' }}>Reason (if not useful)</th>
                </tr>
              </thead>
              <tbody>
                {steps.map((step, index) => (
                  <tr key={index}>
                    <td style={{ textAlign: 'center', verticalAlign: 'middle', fontWeight: '600', width: '60px' }}>
                      {step['step_number'] || step['Step Number'] || index + 1}
                    </td>
                    <td style={{ textAlign: 'left', verticalAlign: 'middle' }}>
                      {step['step_description'] || step['description'] || step['Description'] || 'N/A'}
                    </td>
                    {/* <td>
                      {step['URL'] ? <a href={step['URL']} target="_blank" rel="noopener noreferrer">Link</a> : "N/A"}
                    </td> */}
                    <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                      {step['image_url'] || step['image_s3_key'] ? (
                        <a href={step['image_url'] || step['image_s3_key']} target="_blank" rel="noopener noreferrer">View Image</a>
                      ) : "N/A"}
                    </td>
                    <td style={{ verticalAlign: 'middle', minWidth: '140px' }}>
                      <select className="form-select" onChange={(e) => handleStepFeedback(index, e.target.value)}>
                        <option value="">Select</option>
                        <option value="useful">Useful</option>
                        <option value="not_useful">Not Useful</option>
                      </select>
                    </td>
                    <td style={{ verticalAlign: 'top', paddingTop: '10px' }}>
                      <textarea
                        className="form-control"
                        id={`reason-${index}`}
                        rows="1"
                        placeholder="Optional: describe why..."
                        style={{ resize: 'none', fontSize: '0.875rem', borderRadius: '6px', overflow: 'hidden', minHeight: '36px' }}
                        onInput={(e) => {
                          e.target.style.height = 'auto';
                          e.target.style.height = e.target.scrollHeight + 'px';
                        }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
          
          {/* NEW: Single button to complete and submit feedback */}
          <div className="d-flex justify-content-end mt-3">
            <button
              className="btn btn-primary"
              onClick={() => setShowFeedbackModal(true)}
            >
              Complete & Submit Feedback
            </button>
          </div>
          
          {/* OLD: Module navigation - COMMENTED OUT
          <div className="d-flex justify-content-between mt-3">
            <button
              className="btn btn-secondary"
              onClick={() => navigate(`/resolution?runbook_id=${runbookId}&module_index=${moduleIndex - 1}&incident_number=${incidentNumber}`)}
              disabled={moduleIndex === 0}
            >
              Previous Module
            </button>
            <button
              className="btn btn-primary"
              onClick={() => navigate(`/resolution?runbook_id=${runbookId}&module_index=${moduleIndex + 1}&incident_number=${incidentNumber}`)}
            >
              {moduleIndex + 1 < totalModules ? 'Next Module' : 'Done'}
            </button>
          </div>
          */}
        </div>
      )}

      {/* Feedback Modal */}
      {showFeedbackModal && (
        <div className="feedback-modal-overlay">
          <div className="feedback-modal-content">
            <div className="feedback-modal-header">
              <h3 className="feedback-modal-title">Was this runbook helpful?</h3>
            </div>
            <div className="feedback-modal-body">
              <div className="feedback-option">
                <input
                  type="radio"
                  id="feedbackYes"
                  name="feedback"
                  value="true"
                  checked={runbookFeedback === true}
                  onChange={() => setRunbookFeedback(true)}
                />
                <label htmlFor="feedbackYes">Yes</label>
              </div>
              <div className="feedback-option">
                <input
                  type="radio"
                  id="feedbackNo"
                  name="feedback"
                  value="false"
                  checked={runbookFeedback === false}
                  onChange={() => setRunbookFeedback(false)}
                />
                <label htmlFor="feedbackNo">No</label>
              </div>

              {feedbackError && (
                <div className="feedback-error">
                  Please select a feedback option (Yes or No).
                </div>
              )}

              {runbookFeedback === false && (
                <textarea
                  name="feedback_reason"
                  id="feedback_reason"
                  placeholder="Please let us know why it wasn't helpful..."
                  className="feedback-reason-box"
                  value={runbookFeedbackReason}
                  onChange={(e) => setRunbookFeedbackReason(e.target.value)}
                ></textarea>
              )}
            </div>
            <div className="feedback-modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowFeedbackModal(false)}>Cancel</button>
              <button type="button" className="btn btn-primary" onClick={handleSubmitRunbookFeedback}>Submit Feedback</button>
            </div>
          </div>
        </div>
      )}
      {/* ============== END: UPDATED MODAL JSX ============== */}
    </div>
  );
}

export default Resolution;
